/**
 * NEXUS UI - HoloPanel
 * Base container with angular cuts and holographic glass effect
 */

import React from 'react';
import type { BaseProps, NexusVariant } from './types';

export interface HoloPanelProps extends BaseProps {
  children: React.ReactNode;
  variant?: NexusVariant;
  scanning?: boolean;
  glow?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export const HoloPanel: React.FC<HoloPanelProps> = ({
  children,
  variant = 'cyan',
  scanning = false,
  glow = false,
  header,
  footer,
  className = '',
  style,
}) => {
  const variantColors: Record<NexusVariant, { border: string; glow: string }> = {
    cyan: { border: 'rgba(0, 212, 255, 0.2)', glow: 'rgba(0, 212, 255, 0.15)' },
    blue: { border: 'rgba(59, 130, 246, 0.2)', glow: 'rgba(59, 130, 246, 0.15)' },
    violet: { border: 'rgba(168, 85, 247, 0.2)', glow: 'rgba(168, 85, 247, 0.15)' },
    green: { border: 'rgba(0, 255, 136, 0.2)', glow: 'rgba(0, 255, 136, 0.15)' },
    amber: { border: 'rgba(255, 170, 0, 0.2)', glow: 'rgba(255, 170, 0, 0.15)' },
    red: { border: 'rgba(255, 51, 102, 0.2)', glow: 'rgba(255, 51, 102, 0.15)' },
  };

  const colors = variantColors[variant];

  return (
    <div
      className={`nx-holo-panel ${scanning ? 'nx-holo-panel--scanning' : ''} ${className}`}
      style={{
        '--panel-border': colors.border,
        '--panel-glow': colors.glow,
        boxShadow: glow ? `0 0 30px ${colors.glow}, inset 0 0 30px ${colors.glow}` : undefined,
        ...style,
      } as React.CSSProperties}
    >
      {scanning && <div className="nx-scan-line" />}
      {header && <div className="nx-holo-panel__header">{header}</div>}
      <div className="nx-holo-panel__content">{children}</div>
      {footer && <div className="nx-holo-panel__footer">{footer}</div>}
    </div>
  );
};

export default HoloPanel;
